/**
 * @module Core/AEM
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// Core Libraries
import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

// Services
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { MatchmediaService } from '@core/public';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { AppConfig } from '@root/app.config';

// Interfaces
import { BrowsePlanConfig } from '@core/aem/interfaces/shop/browse-plan.interface';
import { PlanOption } from '@support/api/proxies/responses/api-2117.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';

// Models
import { PlanDetailsResponseModel } from '@experience/shop/plan-details/models/plan-details-response.model';

// Constants
// import { SETTINGS, CHANNELS } from '@environment/settings';
import {
  CONNECT_BY_TMOBILE_DURATION,
  SHARED_BROWSE_CONSTANTS,
} from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { TEXT_CONSTANTS } from '@support/shared/constants/device.constants';
import { PLAN_PRODUCT_CONSTANTS } from '@core/aem/components/plan-product-offering/plan-product-constant/plan-product-offering.constants';
import {
  FamilyDetails,
  PlansDetails,
} from '@experience/shop/plan-details/interfaces/family-details.interface';
import { PlanDetailsService } from '@experience/shop/plan-details/services/plan-details.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { ActivatedRoute, Params } from '@angular/router';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { FccLabelContent } from '@experience/shop/softgoods/interfaces/family-plan.interface';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';

@Component({
  selector: 'tmo-plan-product-offering',
  templateUrl: './plan-product-offering.component.html',
  styleUrls: ['../../../../../style-sheets/components/input-radio.component.scss'],
})
export class PlanProductOfferingComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * Stores plan Id
   */
  public planId: string;

  /**
   * Condition to check seeMore Limit
   */
  public familySeeMore: number;
  public fccLabelLinkUrl: string;
  public fccLabelLinkText: string;

  /**
   * Stores value aem content
   */
  public planDetailAEMContent: BrowsePlanConfig;

  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;

  /**
   * Flag to see open or close modal
   */
  public seeMoreModal: boolean;

  /**
   * Modal to store dynamic data
   */
  public modal: DynamicModal;

  /**
   * Model to plan Family Details
   */
  public planFamilyModel: PlanDetailsResponseModel;

  /**
   * Boolean value to check for the assisted
   */
  // public isAssisted: boolean;

  /**
   * Flag to verify billingType
   */
  public billingPrepaid: boolean;

  /**
   * String value to store selected plan id
   */
  public planOptionSelectorId: string;
  /**
   * Object to store selected plan
   */
  public selectedPlan: PlansDetails;
  /**
   * boolean to check if plan is selected so that short description can be displayed
   */
  public planSelected: boolean;
  /**
   * Next. Complete. Unsubscribe.
   */
  private ngUnsubscribe: Subject<void>;
  public showFourTiles: boolean;
  /**
   * stores selected Family ID
   */
  public selectedFamilyId: string;
  public isNewProductSelectorTemplateEnabled: boolean;
  /**
   * flag to show prepay text in case of multi-month
   */
  public showPrepay: boolean;
  /**
   * stores formatted value for prepay text
   */
  public forMonths: string;
  /**
   * stores prepay message
   */
  public prepayText: string;

  public pricingDisclaimer: string;
  /**
   * Stores heading for selected plan
   */
  public planHeader: string;
  public tMobilePrepaidPlan: string;
  /*
   * promotion banner header text and icon
   */
  public promotionTagIconTemplate: SafeHtml;
  public planPromotions: { [key: string]: string } = {};
  public isMultilineWarning: boolean;
  public autopayDiscountDetailsModal: StaticModal;

  /*
   * Flag to display AutoPay Discount Content
   */
  public showAutoPayDiscountContent: boolean;
  
  constructor(
    private planDetailsService: PlanDetailsService,
    private sharedBrowseService: SharedBrowseService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private matchmediaService: MatchmediaService,
    private objectService: ObjectService,
    public pdlService: PdlService,
    public activatedRoute: ActivatedRoute,
    private featureToggleUtilService: FeatureToggleUtilService,
    private commonUtilService: CommonUtilService,
    private appConfig: AppConfig,
    private sanitizer: DomSanitizer,
    private cartSupportService: CartSupportService
  ) {
    super(pdlService, activatedRoute);
    this.ngUnsubscribe = new Subject<void>();
    this.seeMoreModal = false;
    this.modal = {
      disableDismiss: false,
      disableEsc: false,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'plan-product-offering-modal',
    };
    this.autopayDiscountDetailsModal = {
      disableDismiss: false,
      disableEsc: false,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'autopay-discount-info-modal',
    };
    this.planSelected = false;
    this.isNewProductSelectorTemplateEnabled =
      this.featureToggleUtilService.isNewProductSelectorTemplateEnabled();
    this.showAutoPayDiscountContent = false;
  }

  /**
   * Component initialisation
   */
  public ngOnInit(): void {
    this.loadAEMData();
    this.familySeeMore = this.checkLimit();
    this.planFamilyModel = this.stateStoreUtilsService.getPlanFamilyDetails();
    this.planOptionSelectorId = this.stateStoreUtilsService.getPlansPDPSelectedPlanId();
    this.billingPrepaid =
      this.stateStoreUtilsService.getCustomerBillingType() ===
      SHARED_BROWSE_CONSTANTS.prepaidAPIMap;
    this.planSelected = false;
    if (this.planOptionSelectorId) {
      this.planDetailsService.selectedPlanSubject.next(this.planId);
      this.setSelectedPlan(this.planOptionSelectorId);
    }
    this.activatedRoute.params
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((planParams: Params) => {
        this.selectedFamilyId = planParams.selectedPlanId;
      });
    this.showFourTiles =
      this.selectedFamilyId === SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE ? true : false;
    this.planHeader = this.objectService.get(
      this.planDetailAEMContent,
      'tmoUnlmtRatePlans',
      SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN_HEADING
    );
    const fccContent: FccLabelContent = this.sharedBrowseService.setFccBroadbandFacts(
      this.selectedFamilyId
    );
    this.fccLabelLinkUrl = fccContent.fccLabelUrl;
    this.fccLabelLinkText = fccContent.fccLabelText;
    this.tMobilePrepaidPlan = SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN;
    this.planPromotions = JSON.parse(
      this.appConfig.getConfig('planFamiliesContent')
    ).planPromotions;
    this.promotionTagIconTemplate = this.sanitizer.bypassSecurityTrustHtml(
      this.appConfig.getConfig('svg_tag_empty')
    );
    this.showAutoPayDiscountContent = !!this.appConfig.getConfig('autoPayDiscountEnabled');
    this.isMultilineWarning =
      this.cartSupportService.isMaxLineReached() ||
      !this.cartSupportService.checkForAddALinePermission(PERMISSION_CONSTANTS.ADD_A_LINE);
  }

  /**
   * This method will set plan for selected plan id
   * returns void
   */
  private setSelectedPlan(planOptionSelectorId: string): void {
    this.planFamilyModel.planFamilyDetails.forEach((familyModel: FamilyDetails) => {
      familyModel.planDetails.forEach((plan: PlansDetails) => {
        if (plan.planId === planOptionSelectorId) {
          plan.isSelected = true;
          this.planDetailsService.setUpdatedPlan(plan);
          this.modal.modalName = this.objectService.get(plan, 'planName', '');
        }
      });
    });
  }
  /**
   * Component clean-up
   * returns void
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * This method is used get limit of char on th basis on device type
   * @param None
   * @returns number
   */
  public checkLimit(): number {
    if (this.matchmediaService.isTablet()) {
      return TEXT_CONSTANTS.TABLET_CHAR_LIMIT;
    } else if (this.matchmediaService.isPhone()) {
      return TEXT_CONSTANTS.MOBILE_CHAR_LIMIT;
    }
    return TEXT_CONSTANTS.DESKTOP_CHAR_LIMIT;
  }

  /**
   * Updates the selected plan
   * @param planOptionSelectorId
   * @returns void
   */
  public updateSelectedPlan(plan: PlanOption, selectedPlanId: string): void {
    this.planId = this.objectService.get(plan, 'planId');
    for (
      let i = 0;
      i < this.objectService.get(this.planFamilyModel, 'planFamilyDetails.length', 0);
      i++
    ) {
      this.planSelected = this.objectService
        .get(this.planFamilyModel, `planFamilyDetails.${i}.planDetails`)
        .find((plans: PlansDetails) => {
          if (plans.planId === selectedPlanId) {
            plans.isSelected = true;
          } else {
            plans.isSelected = false;
          }
        });
    }
    this.planDetailsService.selectedPlanSubject.next(this.planId);
    this.planDetailsService.setUpdatedPlan(plan);
    this.stateStoreUtilsService.setPlansPDPSelectedPlanId(this.planId);
    this.modal.modalName = this.objectService.get(plan, 'planName', '');
    this.planOptionSelectorId = this.stateStoreUtilsService.getPlansPDPSelectedPlanId();
  }

  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @param familyDetails
   * @returns void
   */
  public showSeeMoreModal(event: EventEmitter<Object>, familyDetails: PlansDetails): void {
    this.seeMoreModal = true;
    this.triggerEvent = event;
    this.modal.toggleModalEmitter.emit({
      open: true,
      close: false,
      dismissableMask: true,
    });
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'modalLoad');
    this.selectedPlan = familyDetails;
  }

  /**
   * Method to handle Click event
   * @returns void
   */
  public closeModal(): void {
    const target = PLAN_PRODUCT_CONSTANTS.TARGET;
    this.triggerEvent[target].focus();
    this.modal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.seeMoreModal = false;
  }
  /**
   * Load AEM Data
   * return void
   */
  private loadAEMData(): void {
    this.planDetailsService
      .getAemContent()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: BrowsePlanConfig) => {
        this.planDetailAEMContent = data;
        this.autopayDiscountDetailsModal.headerText = this.planDetailAEMContent.autopayDiscountInfoModalHeader;
        this.autopayDiscountDetailsModal.bodyText = this.planDetailAEMContent.autopayDiscountInfoModalBody;
        this.pricingDisclaimer = this.objectService.get(
          JSON.parse(
            this.objectService.get(this.planDetailAEMContent, 'planFamiliesContent', '{}')
          ),
          `${this.selectedFamilyId}.DCC_pricingDisclaimer`,
          ''
        );
      });
  }

  /**
   * Method to handle radio cta functionality
   * @returns void
   */
  public handleRadioClickEvent(event?: string): void {
    switch (true) {
      case event === CONNECT_BY_TMOBILE_DURATION.ONE_MONTH:
        this.showPrepay = false;
        break;
      case event === CONNECT_BY_TMOBILE_DURATION.SIX_MONTH:
        this.showPrepay = true;
        this.forMonths = this.commonUtilService.messageFormatter(
          this.objectService.get(this.planDetailAEMContent, 'forMonths'),
          [CONNECT_BY_TMOBILE_DURATION.SIX_MONTH]
        );
        this.prepayText = this.commonUtilService.messageFormatter(
          this.objectService.get(this.planDetailAEMContent, 'prepayText'),
          [CONNECT_BY_TMOBILE_DURATION.SIX_MONTH, CONNECT_BY_TMOBILE_DURATION.TWENTY_FOUR]
        );
        break;
      case event === CONNECT_BY_TMOBILE_DURATION.TWELVE_MONTH:
        this.showPrepay = true;
        this.forMonths = this.commonUtilService.messageFormatter(
          this.objectService.get(this.planDetailAEMContent, 'forMonths'),
          [CONNECT_BY_TMOBILE_DURATION.TWELVE_MONTH]
        );
        this.prepayText = this.commonUtilService.messageFormatter(
          this.objectService.get(this.planDetailAEMContent, 'prepayText'),
          [CONNECT_BY_TMOBILE_DURATION.TWELVE_MONTH, CONNECT_BY_TMOBILE_DURATION.TWENTY_FOUR]
        );
        break;
      default:
        this.showPrepay = false;
        break;
    }
  }

  /**
   * Method to fetch plans  filtered by duration
   * @returns void
   */
  public getPlansByDuration(plans: PlanOption[]): void {
    this.objectService.set(this.planFamilyModel, 'planFamilyDetails.0.planDetails', plans);
  }

  /**
   * Handle AutoPay discout modal displaying toggle
   * @returns void
   */
  public toggleAutopayDiscountDetailsModal(openModal: boolean): void {
    this.autopayDiscountDetailsModal.toggleModalEmitter.emit({open: openModal, close: !openModal});
  } 

}
